import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { CandidateRating } from '../../../types';
import Input from '../../Form/Input';
import Select from '../../Form/Select';
import TextArea from '../../Form/TextArea';

interface CandidateFormData {
  status: string;
  assignedManagerId: string;
  interviewDate?: string;
  managerFeedback: string;
  notes: string;
  [key: string]: any;
}

interface CandidateManagementSectionProps {
  register: UseFormRegister<CandidateFormData>;
  errors: FieldErrors<CandidateFormData>;
  statusOptions: { value: string; label: string }[];
  managerOptions: { value: string; label: string }[];
  candidateRatings: CandidateRating[];
  totalRating: number;
  onManagerChange: (managerId: string) => void;
  onShowRatingForm: () => void;
}

const CandidateManagementSection: React.FC<CandidateManagementSectionProps> = ({
  register,
  errors,
  statusOptions,
  managerOptions,
  candidateRatings,
  totalRating,
  onManagerChange,
  onShowRatingForm,
}) => {
  return (
    <div>
      <h4 className="text-md font-medium text-gray-900 mb-4">Management & Evaluation</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Status *"
          {...register('status', { required: 'Status is required' })}
          error={errors.status?.message}
          options={statusOptions}
        />

        <Select
          label="Assigned Manager"
          {...register('assignedManagerId')}
          error={errors.assignedManagerId?.message}
          options={managerOptions}
          onChange={(e) => onManagerChange(e.target.value)}
        />

        <Input
          label="Interview Date"
          type="date"
          {...register('interviewDate')}
          error={errors.interviewDate?.message}
        />

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Assessment Rating
          </label>
          <div className="border border-gray-300 rounded-md p-4 space-y-3">
            {candidateRatings.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-900">
                    Current Total Rating: {totalRating}/10
                  </span>
                  <button
                    type="button"
                    onClick={onShowRatingForm}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Update Rating
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {candidateRatings.map((rating) => (
                    <div key={rating.id} className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">{rating.pointName}</span>
                      <span className="font-medium text-gray-900">{rating.score}/10</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-3">
                <p className="text-sm text-gray-500 mb-2">No assessment ratings yet</p>
                <button
                  type="button"
                  onClick={onShowRatingForm}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Assessment Rating
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <TextArea
          label="Manager Feedback"
          {...register('managerFeedback')}
          error={errors.managerFeedback?.message}
          placeholder="Manager's feedback and evaluation notes..."
          rows={3}
        />

        <TextArea
          label="Additional Notes"
          {...register('notes')}
          error={errors.notes?.message}
          placeholder="Any additional notes or observations..."
          rows={3}
        />
      </div>
    </div>
  );
};

export default CandidateManagementSection;
